import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
});

// Attach token from localStorage if present
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return Promise.reject(err);
  }
);

// Auth
export const registerUser = (data) => API.post('/auth/register', data);
export const sendRegistrationOtp = (data) => API.post('/auth/register/send-otp', data);
export const verifyRegistrationOtp = (data) => API.post('/auth/register/verify-otp', data);
export const loginUser = (data) => API.post('/auth/login', data);
export const logoutUser = () => API.post('/auth/logout');
export const getMe = () => API.get('/auth/me');
export const updateProfile = (data) => API.put('/auth/profile', data);
export const getReferrerByCode = (code) => API.get(`/auth/referrer/${code}`);

// Events
export const getEvents = (params) => API.get('/events', { params });
export const getEvent = (id) => API.get(`/events/${id}`);
export const getSlotAvailability = (eventId, date) => API.get(`/events/${eventId}/slots/${date}`);
export const createEvent = (data) => API.post('/events', data);
export const updateEvent = (id, data) => API.put(`/events/${id}`, data);
export const deleteEvent = (id) => API.delete(`/events/${id}`);

// Bookings
export const createBooking = (data) => API.post('/bookings', data);
export const getMyBookings = () => API.get('/bookings/my-bookings');
export const getBooking = (id) => API.get(`/bookings/${id}`);
export const cancelBooking = (id) => API.put(`/bookings/${id}/cancel`);
export const checkAffiliateDiscount = () => API.get('/bookings/check-discount');
export const applyAffiliateCoupon = (couponCode) => API.post('/bookings/apply-coupon', { couponCode });

// Payments
export const createOrder = (data) => API.post('/payments/create-order', data);
export const verifyPayment = (data) => API.post('/payments/verify', data);
export const getReceipt = (bookingId) => API.get(`/payments/receipt/${bookingId}`);
export const downloadInvoice = (bookingId) => API.get(`/payments/invoice/${bookingId}`, { responseType: 'blob' });

// Referrals
export const getMyReferrals = () => API.get('/referrals/my-referrals');
export const getMyEarnings = () => API.get('/referrals/my-earnings');
export const getLeaderboard = () => API.get('/referrals/leaderboard');
export const getMyReferralTree = () => API.get('/referrals/my-tree');

// Rewards
export const getRewards = () => API.get('/rewards');
export const redeemReward = (rewardId, data) => API.post(`/rewards/redeem/${rewardId}`, data);
export const getMyRedemptions = () => API.get('/rewards/my-redemptions');

// Partners
export const submitPartnerApplication = (data) => API.post('/partners/apply', data);
export const getPartnerApplications = (params) => API.get('/partners/applications', { params });
export const updatePartnerStatus = (id, data) => API.put(`/partners/applications/${id}`, data);

// Affiliates
export const getAffiliateDashboard = () => API.get('/affiliates/dashboard');
export const getAffiliateReferrals = () => API.get('/affiliates/referrals');
export const getAdminAffiliateList = () => API.get('/affiliates/admin/list');
export const getAdminAffiliateDetail = (id) => API.get(`/affiliates/admin/${id}`);
export const calculateHonorarium = (data) => API.post('/affiliates/admin/honorarium', data);
export const markHonorariumPaid = (id) => API.put(`/affiliates/admin/honorarium/${id}/pay`);
export const markCommissionPaid = (id) => API.put(`/affiliates/admin/${id}/pay-commission`);

// Glamping
export const submitGlampingEnquiry = (data) => API.post('/glamping/enquiry', data);
export const getGlampingEnquiries = () => API.get('/glamping/enquiries');
export const updateGlampingStatus = (id, data) => API.put(`/glamping/enquiries/${id}`, data);

// MTB Ambassador
export const getCloudinaryUploadSignature = (data) => API.post('/uploads/sign', data);
export const submitAmbassadorEntry = (data) => API.post('/ambassador/submit', data);
export const getMyAmbassadorEntry = () => API.get('/ambassador/my-entry');
export const getAdminAmbassadorEntries = (params) => API.get('/ambassador/admin/list', { params });
export const getAdminAmbassadorEntry = (id) => API.get(`/ambassador/admin/${id}`);
export const updateAmbassadorEntryStatus = (id, data) => API.put(`/ambassador/admin/${id}`, data);

// Contact
export const submitContactEnquiry = (data) => API.post('/contact/enquiry', data);
export const subscribeNewsletter = (data) => API.post('/contact/newsletter', data);
export const getContactEnquiries = () => API.get('/contact/enquiries');

// Admin
export const getAdminDashboard = () => API.get('/admin/dashboard');
export const getAdminRegistrations = (params) => API.get('/admin/registrations', { params });
export const getAdminUsers = () => API.get('/admin/users');
export const getAllUsers = () => API.get('/admin/users');
export const getCommissionConfig = () => API.get('/admin/commission-config');
export const updateCommissionConfig = (data) => API.put('/admin/commission-config', data);
export const updateWhatsAppLink = (data) => API.put('/admin/whatsapp-link', data);
export const createReward = (data) => API.post('/rewards', data);
export const updateReward = (id, data) => API.put(`/rewards/${id}`, data);

export default API;
